/**
 * Extract GPS coordinates and camera bearing from a photo's EXIF data.
 * Reads only the first 128KB of the file (EXIF header).
 */
export function extractExifLocation(file) {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const view = new DataView(e.target.result);
        if (view.getUint16(0) !== 0xffd8) return resolve(null);
        let offset = 2;
        while (offset < view.byteLength - 1) {
          const marker = view.getUint16(offset);
          offset += 2;
          if (marker === 0xffe1) {
            const start = offset + 2;
            if (String.fromCharCode(view.getUint8(start), view.getUint8(start + 1), view.getUint8(start + 2), view.getUint8(start + 3)) !== "Exif") return resolve(null);
            const ts = start + 6;
            const be = view.getUint16(ts) === 0x4d4d;
            const g16 = o => view.getUint16(o, !be);
            const g32 = o => view.getUint32(o, !be);
            const ifd = ts + g32(ts + 4);
            let gOff = 0;
            for (let i = 0; i < g16(ifd); i++) {
              if (g16(ifd + 2 + i * 12) === 0x8825) {
                gOff = ts + g32(ifd + 2 + i * 12 + 8);
                break;
              }
            }
            if (!gOff) return resolve(null);
            let latR = "",
              lonR = "";
            let latV = [],
              lonV = [];
            let bearing = null;
            for (let i = 0; i < g16(gOff); i++) {
              const en = gOff + 2 + i * 12;
              const tag = g16(en);
              const vo = ts + g32(en + 8);
              if (tag === 1) latR = String.fromCharCode(view.getUint8(en + 8));else if (tag === 2) latV = readRational(view, vo, 3, be);else if (tag === 3) lonR = String.fromCharCode(view.getUint8(en + 8));else if (tag === 4) lonV = readRational(view, vo, 3, be);
              // Tag 17 = GPSImgDirection (camera bearing in degrees)
              else if (tag === 17) {
                const dirV = readRational(view, vo, 1, be);
                if (dirV.length === 1) bearing = dirV[0];
              }
            }
            if (latV.length !== 3 || lonV.length !== 3) return resolve(null);
            let lat = latV[0] + latV[1] / 60 + latV[2] / 3600;
            let lon = lonV[0] + lonV[1] / 60 + lonV[2] / 3600;
            if (latR === "S") lat = -lat;
            if (lonR === "W") lon = -lon;
            if (lat === 0 && lon === 0) return resolve(null);
            return resolve({
              latitude: lat,
              longitude: lon,
              bearing
            });
          }
          if ((marker & 0xff00) !== 0xff00) break;
          offset += view.getUint16(offset);
        }
        resolve(null);
      } catch {
        resolve(null);
      }
    };
    reader.onerror = () => resolve(null);
    reader.readAsArrayBuffer(file.slice(0, 128 * 1024));
  });
}

/**
 * Offset a lat/lng position by `meters` in a given `bearing` direction.
 * Uses the Haversine forward formula. Returns original position if bearing is null.
 */
export function offsetPosition(lat, lng, bearing, meters) {
  if (bearing === null) return {
    latitude: lat,
    longitude: lng
  };
  const R = 6_378_137; // Earth radius in meters
  const d = meters / R;
  const b = bearing * Math.PI / 180;
  const lat1 = lat * Math.PI / 180;
  const lng1 = lng * Math.PI / 180;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(b));
  const lng2 = lng1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return {
    latitude: lat2 * 180 / Math.PI,
    longitude: lng2 * 180 / Math.PI
  };
}

/** Approximate distance in meters between two lat/lng points */
export function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6_378_137;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function readRational(view, off, count, be) {
  const v = [];
  for (let i = 0; i < count; i++) {
    const n = view.getUint32(off + i * 8, !be);
    const d = view.getUint32(off + i * 8 + 4, !be);
    v.push(d ? n / d : 0);
  }
  return v;
}